import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import pickBy from 'lodash/pickBy';
import transform from 'lodash/transform';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import Icon from '../icon/Icon';
import Error from '../error/Error';
import Responsive from '../responsive/Responsive';
import Button from '../controls/button/Button';
import Select from '../controls/select/Select';
import Input from '../controls/input/Input';
import Label from '../controls/label/Label';
import { Validator, validators } from '../../utils/helpers/validators';

import './RoutablePoints.scss';

const OTHER_ROUTABLE_POINT_TYPE = 'other';

/**
 * Translations to English of "airport" and "hospital" canonical IDs and their children. Full list can be found https://github.com/mapbox/tag-to-category/blob/master/reference/categories.csv
 */
const poiCategoriesEnglish = {
  airport: new Set(['airport', 'airport gate', 'airport terminal']),
  hospital: new Set(['hospital', 'emergency room']),
};

const ROUTABLE_POINT_TYPES_OPTIONS = [
  { label: 'Pick-up/Drop-off', value: 'pick_up_or_drop_off' },
  { label: 'Parking', value: 'parking' },
  { label: 'Taxi/Ride App Pickup', value: 'ride_share_or_taxi_zone' },
  { label: 'Loading Dock', value: 'loading_dock' },
  {
    label: 'Departure',
    value: 'departure',
    onlyForCategories: poiCategoriesEnglish.airport,
  },
  {
    label: 'Arrival',
    value: 'arrival',
    onlyForCategories: poiCategoriesEnglish.airport,
  },
  {
    label: 'Emergency Room',
    value: 'emergency_room',
    onlyForCategories: poiCategoriesEnglish.hospital,
  },
  { label: 'Other', value: OTHER_ROUTABLE_POINT_TYPE },
];

export const getNewRoutablePoint = (initialPointName) => ({
  name: initialPointName || '',
  type: '',
  otherType: '',
});

const routablePointValidationSchema = {
  name: validators.compose(
    validators.required('Name'),
    validators.maxLength(100, 'Name'),
  ),
  type: validators.required('Type'),
  otherType: validators.compose(
    validators.validateIf(
      validators.required('Other Type'),
      (_, allFieldsValues) =>
        allFieldsValues.type === OTHER_ROUTABLE_POINT_TYPE,
    ),
    validators.maxLength(50, 'Other Type'),
  ),
};
const routablePointValidator = (routablePoint) =>
  pickBy(
    transform(
      routablePointValidationSchema,
      (acc, validator: Validator, key) => {
        acc[key] = validator(routablePoint[key], routablePoint);
      },
      {},
    ),
    (val) => !isNil(val),
  );

export const fieldValidator = (routablePoints) => {
  const errors = routablePoints.map(routablePointValidator);

  if (errors.some((res) => !isEmpty(res))) {
    return errors;
  }
};

export interface RoutablePointsProps {
  className?: string;
  value?;
  onChange;
  activePointIndex;
  initialPointName;
  error;
  disabled;
  maxCount?;
  poiCategories;
  onAddRoutablePoint;
  onEditRoutablePointLocation;
  onRemoveRoutablePoint;
}

const RoutablePoints = ({
  className,
  value = [],
  onChange,
  activePointIndex,
  initialPointName,
  error,
  disabled,
  maxCount = 5,
  poiCategories,
  onAddRoutablePoint,
  onEditRoutablePointLocation,
  onRemoveRoutablePoint,
}: RoutablePointsProps) => {
  const handleAddPointClick = useCallback(() => {
    onChange([...value, getNewRoutablePoint(initialPointName)]);

    if (onAddRoutablePoint) {
      onAddRoutablePoint(value.length);
    }
  }, [value, onChange, onAddRoutablePoint, initialPointName]);

  const handlePointChange = useCallback(
    (idx, newValue, path) =>
      onChange(
        [...value.slice(0, idx), newValue, ...value.slice(idx + 1)],
        `[${idx}].${path}`,
      ),
    [value, onChange],
  );

  const handlePointRemove = useCallback(
    (idx) => {
      const newValue = value.filter((_, index) => index !== idx);
      onChange(newValue, `[${idx}]`);

      if (onRemoveRoutablePoint) {
        onRemoveRoutablePoint(idx);
      }
    },
    [value, onChange, onRemoveRoutablePoint],
  );

  const routablePointTypesOptions = useMemo(() => {
    return ROUTABLE_POINT_TYPES_OPTIONS.filter((option) => {
      if (!option.onlyForCategories) {
        return true;
      }

      if (!Array.isArray(poiCategories)) {
        return false;
      }

      return poiCategories.some((poiCategory: string) => {
        return option.onlyForCategories.has(
          // keep categories standartized
          poiCategory.toLowerCase().replace(/[_-]/, ' '),
        );
      });
    });
  }, [poiCategories]);

  return (
    <div className={classnames('routable-points', className)}>
      <div className="routable-points__points-list">
        {value.map((point, idx) => (
          <div
            key={`point_${idx}`}
            className={classnames('routable-points__point', {
              'routable-points__point_active': idx === activePointIndex,
            })}
          >
            <div className="routable-points__point-field-label-container">
              <Label
                className="routable-points__point-field-label"
                label={`${idx + 1}. Name`}
                required={true}
              />
              <button
                className="routable-points__point-remove"
                aria-label="Remove routable point"
                type="button"
                onClick={() => handlePointRemove(idx)}
              >
                <Icon name="cross" />
              </button>
            </div>
            <Input
              className="routable-points__point-name"
              value={point.name}
              active={idx === activePointIndex}
              hasError={error?.[idx]?.name}
              onChange={({ target: { value } }) =>
                handlePointChange(idx, { ...point, name: value }, 'name')
              }
              disabled={true}
            />
            {error?.[idx]?.name && (
              <Error
                className="routable-points__error routable-points__name-error"
                text={error[idx].name}
              />
            )}

            <Label
              className="routable-points__point-field-label"
              label="Type"
              required={true}
            />
            <Select
              className="routable-points__point-type-select"
              disabled={disabled}
              notSelectedOption={{ label: '', value: '' }}
              options={routablePointTypesOptions}
              value={point.type}
              onChange={(value) =>
                handlePointChange(idx, { ...point, type: value }, 'type')
              }
            />
            {error && error[idx]?.type && (
              <Error
                className="routable-points__error routable-points__type-error"
                text={error[idx].type}
              />
            )}

            {point.type === OTHER_ROUTABLE_POINT_TYPE && (
              <>
                <Label
                  className="routable-points__point-field-label"
                  label="Other Type"
                  required={true}
                />
                <Input
                  className="routable-points__point-other-type"
                  value={point.otherType}
                  hasError={error?.[idx]?.otherType}
                  onChange={({ target: { value } }) =>
                    handlePointChange(
                      idx,
                      { ...point, otherType: value },
                      'otherType',
                    )
                  }
                />
                {error && error[idx]?.otherType && (
                  <Error
                    className="routable-points__error routable-points__other-type-error"
                    text={error[idx].otherType}
                  />
                )}
              </>
            )}
            <Responsive mobile={true}>
              <button
                className="routable-points__point-location-button"
                type="button"
                onClick={() => onEditRoutablePointLocation?.(idx)}
              >
                <span className="poi-form__location-form-toggle-button-icon">
                  <Icon name="pin" />
                </span>
                <span className="poi-form__location-form-toggle-button-text">
                  Set location on map →
                </span>
              </button>
            </Responsive>
          </div>
        ))}
      </div>
      {value.length < maxCount && (
        <Button
          className={classnames(
            'routable-points__add-point-button',
            !value.length && 'routable-points__add-point-button--no-items',
          )}
          type="button"
          appearance="primary"
          disabled={disabled}
          onClick={handleAddPointClick}
        >
          {value.length ? '+ Click to add another point' : 'Set Pickup/Arrival'}
        </Button>
      )}
    </div>
  );
};

export default RoutablePoints;
